<template>
  <div>
    <b-row>
      <b-col cols="6" md="3" v-for="(item, i) in summary" :key="i">
        <b-card class="shadow-sm">
          <h6 class="text-muted">{{ item.title }}</h6>
          <h4 class="mb-0 font-weight-bold">
            {{ formatCurrencyBuiltInFunc(item.value) }}
          </h4>
        </b-card>
      </b-col>
    </b-row>

    <!-- Action Buttons -->
    <div class="d-flex justify-content-end mb-1" style="gap: 8px">
      <b-button variant="primary">Export CSV</b-button>
      <b-button variant="secondary" @click="clearFilters">Clear</b-button>
    </div>

    <b-card>
      <b-row>
        <b-col md="2">
          <b-form-group>
            <label for="dateRange">Date Range </label>
            <b-form-select
              id="dateRange"
              :options="dateRanges"
              v-model="filters.dateRange"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group>
            <label for="startDate">Date From</label>
            <flat-pickr
              v-model="filters.startDate"
              id="startDate"
              class="form-control"
              :config="configFrom"
              placeholder="Date From"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group>
            <label for="endDate">Date To</label>
            <flat-pickr
              v-model="filters.endDate"
              id="endDate"
              class="form-control"
              :config="configTo"
              placeholder="Date To"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group>
            <label for="method">Payment Method</label>
            <b-form-select
              id="method"
              :options="methodOptions"
              v-model="filters.method"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group>
            <label for="minAmount">Min Amount</label>
            <b-form-input type="number" v-model="filters.minAmount" />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group>
            <label for="maxAmount">Max Amount</label>
            <b-form-input type="number" v-model="filters.maxAmount" />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Search Row -->
      <b-row>
        <b-col md="3">
          <b-form-group>
            <label for="searchName">Search Name</label>
            <b-form-input
              id="searchName"
              placeholder="e.g. Jane Doe"
              v-model="filters.name"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group>
            <label for="searchEmail">Search Email</label>
            <b-form-input
              id="searchEmail"
              placeholder="e.g. user@email.com"
              v-model="filters.email"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group>
            <label for="searchUsername">Search Username</label>
            <b-form-input
              id="searchUsername"
              placeholder="e.g. jdoe"
              v-model="filters.username"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group>
            <label for="merchantRef">Transaction ID</label>
            <b-form-input
              placeholder="e.g. ADM-550e8400..."
              v-model="filters.merchantRef"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>
    <!-- 
    <b-button
      variant="success"
      class="float-right mb-2"
      @click="handleOpenAddTrans"
      >+ Add Transaction</b-button
    > -->
    <!-- Transactions Table -->
    <b-table
      :key="tableKey"
      :items="items"
      :fields="fields"
      responsive
      thead-class="text-nowrap"
      :busy="loading"
      show-empty
    >
      <template #empty>
        <div class="text-center text-dark my-2">No matching records found</div>
      </template>
      <template #cell(createdTime)="{ item }">
        <span>
          {{ format(new Date(item.createdTime), "yyyy-MM-dd HH:mm") }}
        </span>
      </template>
      <template #cell(id)="data">
        <b-link>
          {{ data.item.id }}
        </b-link>
      </template>
      <template #cell(createdBy)="{ item }">
        <span>
          {{ item.createdBy }}
        </span>
      </template>
      <template #cell(recipient)="{ item }">
        <span>
          {{ item.recipient || "-" }}
        </span>
      </template>
      <template #cell(username)="{ item }">
        <span>
          {{ item.username }}
        </span>
      </template>
      <template #cell(method)="{ item }">
        <span>
          {{ formatType(item.transactionableType) }}
        </span>
      </template>
      <template #cell(type)="{ item }">
        <span>
          {{ formatType(item.type) }}
        </span>
      </template>
      <template #cell(amount)="{ item }">
        <span>
          {{ item.amount ? formatCurrencyBuiltInFunc(item.amount) : "N/A" }}
        </span>
      </template>

      <template #cell(oldBalance)="{ item }">
        <span>
          {{
            item.oldBalance != null
              ? formatCurrencyBuiltInFunc(item.oldBalance)
              : "N/A"
          }}
        </span>
      </template>
      <template #cell(currentBalance)="{ item }">
        <span>
          {{
            item.oldBalance != null
              ? formatCurrencyBuiltInFunc(item.oldBalance + item.amount)
              : "N/A"
          }}
        </span>
      </template>

      <template #cell(notes)="{ item }">
        <span>
          {{ item.description || "-" }}
        </span>
      </template>
    </b-table>
    <div class="d-flex flex-wrap justify-content-between align-items-center">
      <div class="d-flex align-items-center">
        <span class="mr-1 text-nowrap">Rows per page</span>
        <b-form-select
          v-model="limit"
          :options="limitOptions"
          size="sm"
          class="mr-1"
        />
        <span class="text-nowrap">
          {{ paginationSummary }}
        </span>
      </div>

      <b-pagination
        v-model="page"
        :total-rows="pagination.total"
        :per-page="limit"
        first-number
        last-number
      />
    </div>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCol,
  BFormDatepicker,
  BFormInput,
  BFormSelect,
  BRow,
  BTable,
  BLink,
  BButtonGroup,
  BCard,
  BPagination,
  BModal,
  BFormCheckbox,
  BFormGroup,
  BFormTextarea,
} from "bootstrap-vue";
import { formatCurrencyBuiltInFunc } from "@/helpers/utils";
import { mapState, mapActions } from "vuex";
import flatPickr from "vue-flatpickr-component";
import { debounce } from "debounce";
import { format } from "date-fns";

export default {
  components: {
    BButton,
    BFormInput,
    BFormSelect,
    BFormDatepicker,
    BTable,
    BBadge,
    BRow,
    BCol,
    BLink,
    BButtonGroup,
    BCard,
    BPagination,
    BModal,
    BFormCheckbox,
    BFormGroup,
    BFormTextarea,
    flatPickr,
  },
  data() {
    const statusOptions = [
      "All",
      "Approved",
      "Pending",
      "Failed",
      "Cancelled",
      "Reversed",
      "Flagged",
    ];
    const methodOptions = ["All", "Card", "Bank", "PayID", "Cash"];
    const dateRanges = [
      { value: "custom", text: "Custom" },
      { value: "7days", text: "Last 7 Days" },
      { value: "30days", text: "Last 30 Days" },
      { value: "60days", text: "Last 60 Days" },
      { value: "90days", text: "Last 90 Days" },
    ];
    return {
      tableKey: 0,
      summary: [
        { title: "Total Deposits", value: 19006.03 },
        { title: "Manual Deposits", value: 12905.17 },
        { title: "Adjustments", value: 18539.39 },
        { title: "Average Balance", value: 4714.79 },
      ],
      filters: {
        dateRange: dateRanges[0].value,
        startDate: "",
        endDate: "",
        status: statusOptions[0],
        method: methodOptions[0],
        minAmount: 0,
        maxAmount: null,
        name: "",
        email: "",
        username: "",
        merchantRef: "",
      },
      page: 1,
      limit: 25,
      limitOptions: [10, 25, 50, 100],
      statusOptions,
      methodOptions,
      dateRanges,
      configFrom: {
        altFormat: "d-m-Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },
      configTo: {
        altFormat: "d-m-Y",
        altInput: true,
        dateFormat: "Y-m-d",
        minDate: null,
      },
      fields: [
        { key: "createdTime", label: "Transaction Date" },
        { key: "id", label: "Transaction ID" },
        { key: "createdBy", label: "Merchant Ref." },
        { key: "recipient", label: "Full Name" },
        { key: "username", label: "Username" },
        { key: "method", label: "Transaction Type" },
        { key: "type", label: "Type" },
        { key: "amount", label: "Amount" },
        { key: "tokenSymbol", label: "Currency Type" },
        { key: "oldBalance", label: "Balance Before Deposit" },
        { key: "currentBalance", label: "Balance After Deposit" },
        { key: "notes", label: "Notes" },
      ],
      items: [],
      accountingTypes: ["Payment", "Refund", "Journal", "Adjustment"],
    };
  },
  computed: {
    ...mapState("finance/deposit", ["loading", "pagination", "deposits"]),
    paginationSummary() {
      const p = this.pagination || {};
      const page = Number(p.page) || 1;
      const limit = Number(p.limit) || 10;
      const total = Number(p.total) || 0;
      const from = total === 0 ? 0 : (page - 1) * limit + 1;
      const to = total === 0 ? 0 : Math.min(page * limit, total);
      return total === 0
        ? `Showing 0 of 0 entries`
        : `Showing ${from} - ${to} of ${total} entries`;
    },
  },
  watch: {
    "filters.name"() {
      this.handleSearchDebounced();
    },
    page() {
      this.fetchDeposits();
    },
    limit() {
      this.fetchDeposits();
    },
    "filters.status"(val) {
      this.fetchDeposits();
    },
    "filters.dateRange"(newVal) {
      const rangeMap = {
        "7days": 7,
        "30days": 30,
        "60days": 60,
        "90days": 90,
      };
      if (rangeMap[newVal]) {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - rangeMap[newVal]);
        this.filters.startDate = format(startDate, "yyyy-MM-dd");
        this.filters.endDate = format(endDate, "yyyy-MM-dd");
      } else {
        this.filters.startDate = null;
        this.filters.endDate = null;
      }
    },
    "filters.startDate"(newVal) {
      this.configTo = {
        ...this.configTo,
        minDate: newVal || null,
      };
      this.fetchDeposits();
    },

    "filters.startDate"(newVal) {
      this.configFrom = {
        ...this.configFrom,
        maxDate: newVal || null,
      };
      this.fetchDeposits();
    },
  },
  mounted() {
    this.handleSearchDebounced = debounce(this.handleSearch, 300);
  },
  created() {
    this.fetchDeposits();
  },
  methods: {
    ...mapActions("finance/deposit", ["getDeposits"]),
    formatCurrencyBuiltInFunc,
    format,
    formatType(value) {
      if (!value) return "-";
      const typeMap = {
        deposit: "Deposit",
        withdrawal: "Withdrawal",
        bettip: "Bet-Entry",
        betWin: "Bet-Win",
        betRefund: "Bet-Refund",
        bonusOffer: "Bonus Offer",
      };
      return typeMap[value] || value;
    },
    clearFilters() {
      this.filters = {
        dateRange: this.dateRanges[0].value,
        startDate: null,
        endDate: null,
        status: this.statusOptions[0],
        method: this.methodOptions[0],
        minAmount: 0,
        maxAmount: null,
        name: "",
        email: "",
        username: "",
        merchantRef: "",
      };
    },
    async fetchDeposits() {
      if (this.loading) return;
      try {
        const {
          limit,
          page,
          filters: { startDate, endDate, status },
        } = this;
        const params = {
          type: "deposit",
          ...(!!startDate && { "date[startDate]": startDate }),
          ...(!!endDate && { "date[endDate]": endDate }),

          status: status && status !== "All" ? status : undefined,
          limit,
          page,
        };

        const data = await this.getDeposits(params);
        this.items = data;
      } catch (e) {
        this.items = [];
      }
    },

    updateTableKey() {
      this.tableKey++;
    },
    handleSearch() {
      this.page = 1;
      this.fetchDeposits();
    },
    handleOpenAddTrans() {
      this.$bvModal.show("add-transaction-modal");
    },
  },
};
</script>
